import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { GoogleReview } from "@/lib/schemas";

interface Review {
  id: string;
  reviewer_name: string;
  rating: number;
  review_text: string;
  review_date: string;
  source: string;
  move_type?: string;
  is_featured?: boolean;
}

// Fallback reviews in case database is empty or fails
const fallbackReviews: Omit<Review, 'id'>[] = [
  {
    reviewer_name: "Jacqueline Allen",
    rating: 5,
    review_date: "2026-03-12",
    review_text: "Dan made my experience hiring movers for a long haul move so easy. He took the time to explain everything to me and did not push me into making a rushed decision. Very professional and I appreciate all his help",
    source: "Trustpilot"
  },
  {
    reviewer_name: "Marcie",
    rating: 5,
    review_date: "2026-03-14",
    review_text: "The Movers was very Professional and they did a Good Job. My Over all experience with the Company was excellent",
    source: "Trustpilot"
  },
  {
    reviewer_name: "Faith Ochieng",
    rating: 5,
    review_date: "2026-02-22",
    review_text: "Great experience with LifeTime Movers. Highly recommended!",
    source: "Google"
  },
  {
    reviewer_name: "Faith Waweru",
    rating: 5,
    review_date: "2025-12-22",
    review_text: "Great experience with this moving company. Friendly and professional. Very efficient. Process from start to finish was easy and straightforward.",
    source: "Google"
  },
  {
    reviewer_name: "Mihaela Gradinari",
    rating: 5,
    review_date: "2025-12-01",
    review_text: "Lifetime Movers was wonderful to work with. Sergio and the other 2 guys were kind, careful, and made me feel at ease the whole time. My things arrived quickly and safely in Florida, and the move felt so much easier than expected.",
    source: "Google"
  },
  {
    reviewer_name: "Cristina C.",
    rating: 5,
    review_date: "2025-12-01",
    review_text: "LifeTime Movers made my move surprisingly easy. Very thankful to Sergio for his patience and help. The team was organized, friendly, and took great care wrapping and loading everything. My items arrived in perfect condition in California.",
    source: "Google"
  },
  {
    reviewer_name: "Sahithi Tupuri",
    rating: 5,
    review_date: "2026-02-05",
    review_text: "I had a great experience working with Daniel from the beginning to book. Roemello & Sergio came on time to pack and move everything. They were great! It was super quick and handled very professionally.",
    source: "Google"
  },
  {
    reviewer_name: "Tobby",
    rating: 5,
    review_date: "2026-02-05",
    review_text: "Great moving experience with LifeTime Movers. Professional and efficient team.",
    source: "Google"
  },
  {
    reviewer_name: "Fungai",
    rating: 4,
    review_date: "2026-02-12",
    review_text: "Great service. There was a misunderstanding at delivery but they addressed all the concerns.",
    source: "Google"
  }
];

async function fetchReviews(): Promise<Review[]> {
  const { data, error } = await supabase
    .from('google_reviews_public')
    .select('id, reviewer_name, rating, review_text, review_date, source, move_type, is_featured')
    .order('is_featured', { ascending: false })
    .order('review_date', { ascending: false })
    .limit(10);

  if (error) {
    console.error('Error fetching reviews:', error);
    return fallbackReviews.map((r, i) => ({ ...r, id: `fallback-${i}` }));
  }
  
  if (data && data.length > 0) {
    return data;
  }
  
  return fallbackReviews.map((r, i) => ({ ...r, id: `fallback-${i}` }));
}

async function fetchFeaturedReviews(): Promise<GoogleReview[]> {
  const { data, error } = await supabase
    .from('google_reviews_public')
    .select('id, reviewer_name, rating, review_text, review_date, source, move_type, is_featured')
    .eq('is_featured', true)
    .order('review_date', { ascending: false })
    .limit(5);

  if (error || !data) {
    return [];
  }
  
  return data;
}

/**
 * Hook for fetching all reviews (carousel display)
 * Uses React Query for efficient caching and deduplication
 */
export function useReviews() {
  return useQuery({
    queryKey: ['reviews', 'all'],
    queryFn: fetchReviews,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
  });
}

/**
 * Hook for fetching featured reviews (SEO schema)
 * Separate query for featured-only to avoid blocking initial render
 */
export function useFeaturedReviews(enabled: boolean = true) {
  return useQuery({
    queryKey: ['reviews', 'featured'],
    queryFn: fetchFeaturedReviews,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled,
  });
}

export type { Review };
